import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type Catalog = {
  activities: Array<{
    activityId: string;
    activityTitle: string;
    activityType: string;
    programId: string;
    programTitle: string;
    sessionId?: string | null;
    sessionStartsAt?: string | null;
    sessionEndsAt?: string | null;
    locationName?: string | null;
  }>;
  evaluationDefinitions: Array<{
    definitionId: string;
    name: string;
    method: string;
    scoreMin?: number | null;
    scoreMax?: number | null;
  }>;
  achievementDefinitions: Array<{
    definitionId: string;
    name: string;
    description?: string | null;
  }>;
  credentialDefinitions: Array<{
    definitionId: string;
    name: string;
    description?: string | null;
    achievementDefinitionId: string;
    issuerOrganizationId: string;
  }>;
};

type WorkflowState = {
  participations: Array<{
    participationId: string;
    status: string;
    version: number;
    activityId: string;
    sessionId?: string | null;
  }>;
  evidence: Array<{
    evidenceId: string;
    status: string;
    version: number;
  }>;
  evaluations: Array<{
    evaluationResultId: string;
    status: string;
    version: number;
    definitionId: string;
  }>;
  achievements: Array<{
    achievementId: string;
    status: string;
    version: number;
    definitionId: string;
  }>;
  credentialRequests: Array<{
    requestId: string;
    status: string;
    version: number;
    credentialDefinitionId: string;
    achievementId: string;
  }>;
};

function stateLabel(status: string): string {
  const labels: Record<string,string> = {
    REGISTERED:"등록",
    ATTENDED:"참석",
    COMPLETED:"완료",
    DRAFT:"작성 중",
    SUBMITTED:"제출",
    VERIFIED:"검증 완료",
    CONFIRMED:"확정",
    CANDIDATE:"성취 후보",
    REQUESTED:"발급 요청",
    APPROVED:"발급 승인",
    ISSUED:"발급 완료",
    FAILED:"발급 실패"
  };
  return labels[status] || status;
}

export function CenterWorkflowActions({
  client,
  schoolId,
  learnerId,
  hasCredential,
  onChanged
}: {
  client: PlatformClient;
  schoolId: string;
  learnerId: string;
  hasCredential: boolean;
  onChanged(): void;
}) {
  const [catalog,setCatalog]=useState<Catalog | null>(null);
  const [state,setState]=useState<WorkflowState | null>(null);
  const [loading,setLoading]=useState(true);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");

  const [activityId,setActivityId]=useState("");
  const [evaluationDefinitionId,setEvaluationDefinitionId]=useState("");
  const [achievementDefinitionId,setAchievementDefinitionId]=useState("");
  const [credentialDefinitionId,setCredentialDefinitionId]=useState("");
  const [score,setScore]=useState("85");
  const [evidenceTitle,setEvidenceTitle]=useState("활동 결과물");
  const [evidenceNarrative,setEvidenceNarrative]=useState("");

  async function load() {
    setLoading(true);
    setError("");

    const [catalogResult,stateResult]=await Promise.all([
      client.rpc("center_workflow_catalog",{
        p_school_id:schoolId,
        p_learner_id:learnerId
      }),
      client.rpc("center_workflow_state",{
        p_school_id:schoolId,
        p_learner_id:learnerId
      })
    ]);

    if(catalogResult.error){
      setError(catalogResult.error.message);
      setLoading(false);
      return;
    }
    if(stateResult.error){
      setError(stateResult.error.message);
      setLoading(false);
      return;
    }

    const nextCatalog=catalogResult.data as Catalog;
    const nextState=stateResult.data as WorkflowState;

    setCatalog(nextCatalog);
    setState(nextState);

    if(!activityId && nextCatalog.activities[0]){
      setActivityId(nextCatalog.activities[0].activityId);
    }
    if(!evaluationDefinitionId && nextCatalog.evaluationDefinitions[0]){
      setEvaluationDefinitionId(nextCatalog.evaluationDefinitions[0].definitionId);
    }
    if(!achievementDefinitionId && nextCatalog.achievementDefinitions[0]){
      setAchievementDefinitionId(nextCatalog.achievementDefinitions[0].definitionId);
    }

    setLoading(false);
  }

  useEffect(()=>{
    setCatalog(null);
    setState(null);
    setActivityId("");
    setEvaluationDefinitionId("");
    setAchievementDefinitionId("");
    setCredentialDefinitionId("");
    setEvidenceTitle("활동 결과물");
    setEvidenceNarrative("");
    void load();
  },[learnerId,schoolId]);

  const selectedActivity=useMemo(
    ()=>catalog?.activities.find((item)=>item.activityId===activityId) || null,
    [catalog,activityId]
  );

  const latestParticipation=state?.participations[0] || null;
  const latestEvidence=state?.evidence[0] || null;
  const latestEvaluation=state?.evaluations[0] || null;
  const latestAchievement=state?.achievements[0] || null;
  const latestRequest=state?.credentialRequests[0] || null;

  const matchingCredentialDefinitions=useMemo(()=>{
    if(!catalog || !latestAchievement) return [];
    return catalog.credentialDefinitions.filter(
      (item)=>item.achievementDefinitionId===latestAchievement.definitionId
    );
  },[catalog,latestAchievement]);

  useEffect(()=>{
    if(matchingCredentialDefinitions.length===0){
      setCredentialDefinitionId("");
      return;
    }

    if(!matchingCredentialDefinitions.some((item)=>item.definitionId===credentialDefinitionId)){
      setCredentialDefinitionId(matchingCredentialDefinitions[0].definitionId);
    }
  },[matchingCredentialDefinitions,credentialDefinitionId]);

  async function run(key:string,action:()=>PromiseLike<{error?: {message:string} | null}>){
    setBusy(key);
    setError("");
    try{
      const result=await action();
      if(result.error) throw new Error(result.error.message);
      await load();
      onChanged();
    }catch(err){
      setError(err instanceof Error ? err.message : "업무 처리 중 오류가 발생했습니다.");
    }finally{
      setBusy("");
    }
  }

  if(loading){
    return (
      <section className="workflow-panel">
        <div className="panel-title"><strong>업무 진행</strong><span>상태 확인 중…</span></div>
      </section>
    );
  }

  if(!catalog || !state){
    return null;
  }

  let body: React.ReactNode;

  if(hasCredential){
    body=(
      <WorkflowDone
        title="디지털 자격증명 발급 완료"
        description="이 학생의 Golden Path가 자격증명 발급까지 완료되었습니다."
      />
    );
  }else if(latestRequest && ["REQUESTED","APPROVED"].includes(latestRequest.status)){
    body=(
      <WorkflowDone
        title={latestRequest.status==="APPROVED" ? "발급 승인 완료" : "발급 승인 대기"}
        description="Center의 역할은 발급 요청까지입니다. 최종 서명·발급은 Admin/Issuer에서 처리합니다."
      />
    );
  }else if(latestAchievement?.status==="CONFIRMED"){
    body=(
      <WorkflowStep
        number="07"
        title="디지털 자격증명 발급 요청"
        description="확정된 성취를 기준으로 발급 요청을 생성합니다."
      >
        {matchingCredentialDefinitions.length ? (
          <>
            <label>
              자격증명
              <select value={credentialDefinitionId} onChange={(event)=>setCredentialDefinitionId(event.target.value)}>
                {matchingCredentialDefinitions.map((item)=>(
                  <option key={item.definitionId} value={item.definitionId}>{item.name}</option>
                ))}
              </select>
            </label>
            <button
              className="primary compact-button"
              disabled={!credentialDefinitionId || busy!==""}
              onClick={()=>run("credential",()=>client.rpc("center_request_credential",{
                p_school_id:schoolId,
                p_achievement_id:latestAchievement.achievementId,
                p_credential_definition_id:credentialDefinitionId,
                p_idempotency_key:crypto.randomUUID()
              }))}
            >
              {busy==="credential" ? "요청 중…" : "발급 요청"}
            </button>
          </>
        ):<p className="workflow-hint">이 성취에 연결된 발행 가능한 자격증명 정의가 없습니다.</p>}
      </WorkflowStep>
    );
  }else if(latestAchievement?.status==="CANDIDATE"){
    body=(
      <WorkflowStep
        number="06"
        title="성취 확정"
        description="연결된 평가 결과와 검증 증빙이 기준을 충족하는지 서버에서 다시 확인합니다."
      >
        <button
          className="primary compact-button"
          disabled={busy!==""}
          onClick={()=>run("achievement-confirm",()=>client.rpc("center_confirm_achievement",{
            p_school_id:schoolId,
            p_achievement_id:latestAchievement.achievementId,
            p_expected_version:latestAchievement.version,
            p_reason:"Center에서 성취 기준 확인"
          }))}
        >
          {busy==="achievement-confirm" ? "확인 중…" : "성취 확정"}
        </button>
      </WorkflowStep>
    );
  }else if(latestEvaluation?.status==="CONFIRMED"){
    body=(
      <WorkflowStep
        number="05"
        title="성취 후보 생성"
        description="확정된 평가와 검증 증빙을 성취 기준에 연결합니다."
      >
        {catalog.achievementDefinitions.length ? (
          <>
            <label>
              성취 기준
              <select value={achievementDefinitionId} onChange={(event)=>setAchievementDefinitionId(event.target.value)}>
                {catalog.achievementDefinitions.map((item)=>(
                  <option key={item.definitionId} value={item.definitionId}>{item.name}</option>
                ))}
              </select>
            </label>
            <button
              className="primary compact-button"
              disabled={!achievementDefinitionId || busy!==""}
              onClick={()=>run("achievement-create",()=>client.rpc("center_create_achievement_candidate",{
                p_school_id:schoolId,
                p_learner_id:learnerId,
                p_definition_id:achievementDefinitionId,
                p_period_key:String(new Date().getFullYear())
              }))}
            >
              {busy==="achievement-create" ? "생성 중…" : "성취 후보 생성"}
            </button>
          </>
        ):<p className="workflow-hint">발행된 성취 기준이 없습니다.</p>}
      </WorkflowStep>
    );
  }else if(latestEvaluation?.status==="DRAFT"){
    body=(
      <WorkflowStep
        number="04"
        title="평가 결과 확정"
        description="작성된 평가 결과를 교사가 최종 확인합니다."
      >
        <button
          className="primary compact-button"
          disabled={busy!==""}
          onClick={()=>run("evaluation-confirm",()=>client.rpc("center_confirm_evaluation",{
            p_school_id:schoolId,
            p_result_id:latestEvaluation.evaluationResultId,
            p_expected_version:latestEvaluation.version,
            p_reason:"Center에서 평가 결과 확인"
          }))}
        >
          {busy==="evaluation-confirm" ? "확인 중…" : "평가 결과 확정"}
        </button>
      </WorkflowStep>
    );
  }else if(latestEvidence?.status==="VERIFIED"){
    body=(
      <WorkflowStep
        number="04"
        title="평가 결과 등록"
        description="검증된 활동 증빙을 바탕으로 평가 결과를 기록합니다."
      >
        {catalog.evaluationDefinitions.length ? (
          <div className="workflow-form-grid">
            <label>
              평가 기준
              <select value={evaluationDefinitionId} onChange={(event)=>setEvaluationDefinitionId(event.target.value)}>
                {catalog.evaluationDefinitions.map((item)=>(
                  <option key={item.definitionId} value={item.definitionId}>{item.name}</option>
                ))}
              </select>
            </label>
            <label>
              점수
              <input
                inputMode="decimal"
                value={score}
                onChange={(event)=>setScore(event.target.value)}
                placeholder="0~100"
              />
            </label>
            <button
              className="primary compact-button"
              disabled={!evaluationDefinitionId || !score || busy!==""}
              onClick={()=>run("evaluation-create",()=>client.rpc("center_create_evaluation",{
                p_school_id:schoolId,
                p_learner_id:learnerId,
                p_definition_id:evaluationDefinitionId,
                p_numeric_score:Number(score),
                p_narrative:"Center 업무 화면에서 등록"
              }))}
            >
              {busy==="evaluation-create" ? "등록 중…" : "평가 등록"}
            </button>
          </div>
        ):<p className="workflow-hint">발행된 평가 기준이 없습니다.</p>}
      </WorkflowStep>
    );
  }else if(latestEvidence?.status==="SUBMITTED"){
    body=(
      <WorkflowStep
        number="03"
        title="활동 증빙 검증"
        description="제출된 증빙이 실제 활동 결과를 대표하는지 확인합니다."
      >
        <button
          className="primary compact-button"
          disabled={busy!==""}
          onClick={()=>run("evidence-verify",()=>client.rpc("center_transition_evidence",{
            p_school_id:schoolId,
            p_evidence_id:latestEvidence.evidenceId,
            p_expected_version:latestEvidence.version,
            p_to_status:"VERIFIED",
            p_reason:"Center에서 활동 증빙 검증"
          }))}
        >
          {busy==="evidence-verify" ? "검증 중…" : "증빙 검증"}
        </button>
      </WorkflowStep>
    );
  }else if(latestEvidence?.status==="DRAFT"){
    body=(
      <WorkflowStep
        number="03"
        title="활동 증빙 제출"
        description="작성된 증빙을 검증 단계로 보냅니다."
      >
        <button
          className="primary compact-button"
          disabled={busy!==""}
          onClick={()=>run("evidence-submit",()=>client.rpc("center_transition_evidence",{
            p_school_id:schoolId,
            p_evidence_id:latestEvidence.evidenceId,
            p_expected_version:latestEvidence.version,
            p_to_status:"SUBMITTED",
            p_reason:"Center에서 활동 증빙 제출"
          }))}
        >
          {busy==="evidence-submit" ? "제출 중…" : "증빙 제출"}
        </button>
      </WorkflowStep>
    );
  }else if(latestParticipation?.status==="COMPLETED" || latestParticipation?.status==="ATTENDED"){
    if(latestParticipation.status==="ATTENDED"){
      body=(
        <WorkflowStep
          number="02"
          title="활동 완료 처리"
          description="참석한 활동을 완료 상태로 전환합니다."
        >
          <button
            className="primary compact-button"
            disabled={busy!==""}
            onClick={()=>run("participation-complete",()=>client.rpc("center_transition_participation",{
              p_school_id:schoolId,
              p_participation_id:latestParticipation.participationId,
              p_expected_version:latestParticipation.version,
              p_to_status:"COMPLETED",
              p_reason:"Center에서 활동 완료 처리"
            }))}
          >
            {busy==="participation-complete" ? "처리 중…" : "활동 완료"}
          </button>
        </WorkflowStep>
      );
    }else{
      body=(
        <WorkflowStep
          number="03"
          title="활동 증빙 등록"
          description="완료된 활동에서 확인할 수 있는 결과와 관찰 내용을 기록합니다."
        >
          <div className="workflow-form-grid">
            <label>
              증빙 제목
              <input value={evidenceTitle} onChange={(event)=>setEvidenceTitle(event.target.value)} />
            </label>
            <label className="wide">
              간단한 기록
              <textarea
                value={evidenceNarrative}
                onChange={(event)=>setEvidenceNarrative(event.target.value)}
                placeholder="학생이 무엇을 수행했고 무엇이 확인되었는지 기록하세요."
                rows={3}
              />
            </label>
            <button
              className="primary compact-button"
              disabled={!evidenceTitle.trim() || busy!==""}
              onClick={()=>run("evidence-create",()=>client.rpc("center_create_evidence",{
                p_school_id:schoolId,
                p_learner_id:learnerId,
                p_participation_id:latestParticipation.participationId,
                p_evidence_type:"PROJECT",
                p_title:evidenceTitle.trim(),
                p_narrative:evidenceNarrative
              }))}
            >
              {busy==="evidence-create" ? "등록 중…" : "증빙 등록"}
            </button>
          </div>
        </WorkflowStep>
      );
    }
  }else if(latestParticipation?.status==="REGISTERED"){
    body=(
      <WorkflowStep
        number="02"
        title="활동 참석 처리"
        description="학생의 실제 참여가 확인되면 참석 상태로 전환합니다."
      >
        <button
          className="primary compact-button"
          disabled={busy!==""}
          onClick={()=>run("participation-attend",()=>client.rpc("center_transition_participation",{
            p_school_id:schoolId,
            p_participation_id:latestParticipation.participationId,
            p_expected_version:latestParticipation.version,
            p_to_status:"ATTENDED",
            p_reason:"Center에서 활동 참석 확인"
          }))}
        >
          {busy==="participation-attend" ? "처리 중…" : "참석 처리"}
        </button>
      </WorkflowStep>
    );
  }else{
    body=(
      <WorkflowStep
        number="01"
        title="프로그램 참여 등록"
        description="학생이 참여할 프로그램 활동과 세션을 선택합니다."
      >
        {catalog.activities.length ? (
          <>
            <label>
              활동
              <select value={activityId} onChange={(event)=>setActivityId(event.target.value)}>
                {catalog.activities.map((item)=>(
                  <option key={`${item.activityId}-${item.sessionId || "none"}`} value={item.activityId}>
                    {item.programTitle} · {item.activityTitle}
                  </option>
                ))}
              </select>
            </label>
            {selectedActivity?.locationName && <p className="workflow-hint">장소: {selectedActivity.locationName}</p>}
            <button
              className="primary compact-button"
              disabled={!selectedActivity || busy!==""}
              onClick={()=>run("participation-register",()=>client.rpc("center_register_participation",{
                p_school_id:schoolId,
                p_learner_id:learnerId,
                p_activity_id:selectedActivity?.activityId,
                p_session_id:selectedActivity?.sessionId || null
              }))}
            >
              {busy==="participation-register" ? "등록 중…" : "참여 등록"}
            </button>
          </>
        ):<p className="workflow-hint">현재 등록 가능한 프로그램 활동이 없습니다.</p>}
      </WorkflowStep>
    );
  }

  return (
    <section className="workflow-panel">
      <div className="panel-title">
        <strong>업무 진행</strong>
        <span>{latestRequest ? stateLabel(latestRequest.status) : "다음 단계 자동 안내"}</span>
      </div>
      {error && <p className="error">{error}</p>}
      {body}
    </section>
  );
}

function WorkflowStep({
  number,
  title,
  description,
  children
}:{
  number:string;
  title:string;
  description:string;
  children:React.ReactNode;
}){
  return (
    <div className="workflow-step">
      <div className="workflow-step-copy">
        <span className="workflow-number">{number}</span>
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
      </div>
      <div className="workflow-controls">{children}</div>
    </div>
  );
}

function WorkflowDone({title,description}:{title:string;description:string}){
  return (
    <div className="workflow-done">
      <span>✓</span>
      <div>
        <strong>{title}</strong>
        <p>{description}</p>
      </div>
    </div>
  );
}
